<template>
  <div class="feedback-management min-h-screen bg-gray-50 p-6">
    <!-- Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="bg-white/20 rounded-lg p-3">
              <i class="el-icon-chat-dot-round text-3xl"></i>
            </div>
            <div>
              <h1 class="text-3xl font-bold">Feedback Management {{ currentUserId }}</h1>
              <p class="text-purple-100 mt-1">Kelola dan pantau feedback pengguna</p>
            </div>
          </div>
          <div class="bg-white/20 rounded-lg px-4 py-2">
            <span class="text-2xl font-bold">{{ feedbackList.length }}</span>
            <p class="text-xs text-purple-100">Total Feedback</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filter -->
    <div class="bg-white rounded-xl shadow-md p-4 mb-6">
      <div class="flex items-center space-x-4">
        <el-input
          v-model="searchQuery"
          placeholder="Cari feedback berdasarkan body atau user..."
          prefix-icon="el-icon-search"
          clearable
          class="flex-1"
        >
        </el-input>
        <el-button type="success" icon="el-icon-plus" @click="openCreateDialog">
          Tambah Feedback
        </el-button>
        <el-button type="primary" icon="el-icon-refresh" @click="loadFeedbackData">
          Refresh
        </el-button>
      </div>
    </div>

    <!-- Feedback List -->
    <div v-if="loading" class="text-center py-12">
      <i class="el-icon-loading text-4xl text-purple-600"></i>
      <p class="text-gray-500 mt-4">Memuat data feedback...</p>
    </div>

    <div v-else-if="filteredFeedbackList.length === 0" class="bg-white rounded-xl shadow-md p-12 text-center">
      <i class="el-icon-document-delete text-6xl text-gray-300 mb-4"></i>
      <h3 class="text-xl font-semibold text-gray-600 mb-2">Tidak Ada Feedback</h3>
      <p class="text-gray-400">{{ searchQuery ? 'Tidak ada feedback yang cocok dengan pencarian Anda.' : 'Belum ada feedback yang tersedia.' }}</p>
    </div>

    <div v-else class="masonry">
      <div v-for="feedback in filteredFeedbackList" :key="feedback.id">
        <div class="card">
          <div class="flex justify-between items-center mb-2.5">
            <div>
              <p class="font-light text-xs text-gray-600 p-0 mx-0 mt-0 mb-0"><i class="el-icon-time"></i> {{ formatDate(feedback.createdAt) }}</p>
            </div>
            <div class="flex justify-center gap-2">
              <p v-if="feedback.userUuid === currentUserId" class="text-xs cursor-pointer hover:text-yellow-500 p-0 m-0" @click="editFeedback(feedback)"><i class="el-icon-edit"></i></p>
              <p v-if="feedback.userUuid === currentUserId" class="text-xs cursor-pointer hover:text-red-500 p-0 m-0" @click="openDeleteDialog(feedback)"><i class="el-icon-delete-solid"></i></p>
            </div>
          </div>
          <p class="font-light text-base p-0 m-0">{{ feedback.body }}</p>
          <p class="font-light text-xs text-gray-500 p-0 mx-0 mt-3 mb-0"><i class="el-icon-user"></i> {{ feedback.createdBy }}</p>
        </div>
      </div>
    </div>

    <!-- Detail Dialog -->
    <el-dialog
      :visible.sync="detailDialog"
      width="600px"
      :close-on-click-modal="false"
    >
      <div slot="title" class="bg-gradient-to-r from-purple-600 to-blue-600 -m-5 mb-0 p-5 text-white rounded-t-lg">
        <div class="flex items-center space-x-3">
          <i class="el-icon-document text-2xl"></i>
          <div>
            <h3 class="text-xl font-bold">Detail Feedback</h3>
            <p class="text-sm text-purple-100">ID: #{{ detailData.id }}</p>
          </div>
        </div>
      </div>

      <div class="py-4">
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-purple-50 p-4 rounded-lg">
              <label class="text-xs font-semibold text-purple-600 uppercase block mb-1">User ID</label>
              <p class="text-gray-800 font-medium">{{ detailData.userId }}</p>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg">
              <label class="text-xs font-semibold text-blue-600 uppercase block mb-1">Created By</label>
              <p class="text-gray-800 font-medium">{{ detailData.createdBy || '-' }}</p>
            </div>
          </div>

          <div class="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
            <label class="text-xs font-semibold text-purple-600 uppercase block mb-2">Feedback Body</label>
            <p class="text-gray-700 leading-relaxed">{{ detailData.body }}</p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="bg-green-50 p-4 rounded-lg">
              <label class="text-xs font-semibold text-green-600 uppercase block mb-1">Created At</label>
              <p class="text-gray-800 text-sm">{{ formatDate(detailData.createdAt) }}</p>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg">
              <label class="text-xs font-semibold text-blue-600 uppercase block mb-1">Updated At</label>
              <p class="text-gray-800 text-sm">{{ formatDate(detailData.updatedAt) }}</p>
            </div>
          </div>

          <div v-if="detailData.updatedBy" class="bg-purple-50 p-4 rounded-lg">
            <label class="text-xs font-semibold text-purple-600 uppercase block mb-1">Updated By</label>
            <p class="text-gray-800 font-medium">{{ detailData.updatedBy }}</p>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialog = false">Tutup</el-button>
        <el-button type="primary" icon="el-icon-edit" @click="editFromDetail">Edit Feedback</el-button>
      </div>
    </el-dialog>

    <!-- Edit Dialog -->
    <el-dialog
      :visible.sync="editDialog"
      width="600px"
      :close-on-click-modal="false"
    >
      <div slot="title" class="bg-gradient-to-r from-orange-600 to-yellow-600 -m-5 mb-0 p-5 text-white rounded-t-lg">
        <div class="flex items-center space-x-3">
          <i class="el-icon-edit text-2xl"></i>
          <div>
            <h3 class="text-xl font-bold">Edit Feedback</h3>
            <p class="text-sm text-orange-100">ID: #{{ editForm.id }}</p>
          </div>
        </div>
      </div>

      <el-form :model="editForm" :rules="editFormRules" ref="editFormRef" label-position="top" class="py-4">
        <el-form-item label="Feedback Body" prop="body">
          <el-input
            type="textarea"
            v-model="editForm.body"
            :rows="8"
            placeholder="Masukkan feedback..."
            show-word-limit
            maxlength="500"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialog = false">Batal</el-button>
        <el-button type="primary" icon="el-icon-check" @click="submitEdit" :loading="submitting">
          Simpan Perubahan
        </el-button>
      </div>
    </el-dialog>

    <!-- Delete Dialog -->
    <el-dialog
      :visible.sync="deleteDialog"
      width="500px"
      :close-on-click-modal="false"
    >
      <div slot="title" class="bg-gradient-to-r from-red-600 to-pink-600 -m-5 mb-0 p-5 text-white rounded-t-lg">
        <div class="flex items-center space-x-3">
          <i class="el-icon-warning text-2xl"></i>
          <div>
            <h3 class="text-xl font-bold">Konfirmasi Hapus</h3>
            <p class="text-sm text-red-100">Tindakan ini tidak dapat dibatalkan</p>
          </div>
        </div>
      </div>

      <div class="py-4">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p class="text-gray-700 mb-3">Apakah Anda yakin ingin menghapus feedback ini?</p>
          <div class="space-y-2 text-sm">
            <p><strong class="text-gray-600">ID:</strong> #{{ deleteData.id }}</p>
            <p><strong class="text-gray-600">User ID:</strong> {{ deleteData.userId }}</p>
            <p><strong class="text-gray-600">Created By:</strong> {{ deleteData.createdBy || '-' }}</p>
            <p><strong class="text-gray-600">Body:</strong> {{ deleteData.body }}</p>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialog = false">Batal</el-button>
        <el-button type="danger" icon="el-icon-delete" @click="confirmDelete" :loading="deleting">
          Hapus Feedback
        </el-button>
      </div>
    </el-dialog>

    <!-- Create Dialog -->
    <el-dialog
      :visible.sync="createDialog"
      width="600px"
      :close-on-click-modal="false"
    >
      <div slot="title" class="bg-gradient-to-r from-green-600 to-teal-600 -m-5 mb-0 p-5 text-white rounded-t-lg">
        <div class="flex items-center space-x-3">
          <i class="el-icon-plus text-2xl"></i>
          <div>
            <h3 class="text-xl font-bold">Tambah Feedback Baru</h3>
            <p class="text-sm text-green-100">Buat feedback baru untuk sistem</p>
          </div>
        </div>
      </div>

      <el-form :model="createForm" :rules="createFormRules" ref="createFormRef" label-position="top" class="py-4">
        <el-form-item label="Feedback Body" prop="body">
          <el-input
            type="textarea"
            v-model="createForm.body"
            :rows="8"
            placeholder="Masukkan feedback..."
            show-word-limit
            maxlength="500"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialog = false">Batal</el-button>
        <el-button type="success" icon="el-icon-check" @click="submitCreate" :loading="creating">
          Simpan Feedback
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'FeedbackManagementHome',
  data() {
    return {
      loading: false,
      searchQuery: '',
      detailDialog: false,
      detailData: {},
      editDialog: false,
      editForm: {
        id: null,
        body: ''
      },
      editFormRules: {
        body: [
          { required: true, message: 'Feedback body harus diisi', trigger: 'blur' },
          { min: 10, message: 'Feedback minimal 10 karakter', trigger: 'blur' },
          { max: 500, message: 'Feedback maksimal 500 karakter', trigger: 'blur' }
        ]
      },
      submitting: false,
      deleteDialog: false,
      deleteData: {},
      deleting: false,
      createDialog: false,
      createForm: {
        body: ''
      },
      createFormRules: {
        body: [
          { required: true, message: 'Feedback body harus diisi', trigger: 'blur' },
          { min: 10, message: 'Feedback minimal 10 karakter', trigger: 'blur' },
          { max: 500, message: 'Feedback maksimal 500 karakter', trigger: 'blur' }
        ]
      },
      creating: false
    };
  },
  computed: {
    ...mapState('feedback', {
      storeLoading: 'loading',
      storeFeedback: 'feedback'
    }),
    ...mapState('user', {
      currentUser: 'user'
    }),
    currentUserId() {
      // Ambil dari root state.id yang di-set dari get_roles (commit("set_id", cd.id))
      return this.$store.state.id;
    },
    feedbackList() {
      return this.storeFeedback || [];
    },
    filteredFeedbackList() {
      if (!this.searchQuery) {
        return this.feedbackList;
      }
      const query = this.searchQuery.toLowerCase();
      return this.feedbackList.filter(feedback => {
        return (
          feedback.body.toLowerCase().includes(query) ||
          feedback.createdBy?.toLowerCase().includes(query) ||
          feedback.updatedBy?.toLowerCase().includes(query) ||
          feedback.userId.toString().includes(query)
        );
      });
    }
  },
  created() {
    this.getUserData();
    this.loadFeedbackData();
  },
  methods: {
    ...mapActions('feedback', ['fetchFeedback', 'updateFeedback', 'createFeedback', 'deleteFeedback']),
    ...mapActions('user', ['get_user']),
    getUserData() {
      this.get_user().catch(error => {
        console.error('Error loading user data:', error);
      });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    },
    loadFeedbackData() {
      this.loading = true;
      
      this.fetchFeedback()
        .then((response) => {
          this.$message.success(response.message || 'Data feedback berhasil dimuat!');
          this.loading = false;
        })
        .catch((error) => {
          this.$message.error('Gagal memuat data feedback: ' + (error.message || 'Unknown error'));
          this.loading = false;
        });
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    viewDetail(feedback) {
      this.detailData = { ...feedback };
      this.detailDialog = true;
    },

    editFeedback(feedback) {
      this.editForm = {
        id: feedback.id,
        body: feedback.body
      };
      this.editDialog = true;
    },

    editFromDetail() {
      this.detailDialog = false;
      this.editFeedback(this.detailData);
    },

    submitEdit() {
      this.$refs.editFormRef.validate((valid) => {
        if (valid) {
          this.submitting = true;
          
          this.updateFeedback({
            id: this.editForm.id,
            body: this.editForm.body
          })
            .then((response) => {
              this.$message.success(response.message || 'Feedback berhasil diupdate!');
              this.editDialog = false;
              this.loadFeedbackData();
              this.submitting = false;
            })
            .catch((error) => {
              this.$message.error('Gagal update feedback: ' + (error.message || 'Unknown error'));
              this.submitting = false;
            });
        }
      });
    },

    openDeleteDialog(feedback) {
      this.deleteData = { ...feedback };
      this.deleteDialog = true;
    },

    confirmDelete() {
      this.deleting = true;
      
      this.$store.dispatch('feedback/deleteFeedback', {
        id: this.deleteData.id
      })
        .then((response) => {
          this.$message.success(response.message || 'Feedback berhasil dihapus!');
          this.deleteDialog = false;
          this.loadFeedbackData();
          this.deleting = false;
        })
        .catch((error) => {
          this.$message.error('Gagal menghapus feedback: ' + (error.message || 'Unknown error'));
          this.deleting = false;
        });
    },

    openCreateDialog() {
      this.createForm = {
        body: ''
      };
      this.createDialog = true;
    },

    submitCreate() {
      this.$refs.createFormRef.validate((valid) => {
        if (valid) {
          this.creating = true;
          
          this.createFeedback({
            body: this.createForm.body
          })
            .then((response) => {
              this.$message.success(response.message || 'Feedback berhasil dibuat!');
              this.createDialog = false;
              this.loadFeedbackData();
              this.creating = false;
            })
            .catch((error) => {
              this.$message.error('Gagal membuat feedback: ' + (error.message || 'Unknown error'));
              this.creating = false;
            });
        }
      });
    }
  }
};
</script>

<style scoped>
.feedback-management {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

/* Custom scrollbar untuk textarea */
::v-deep .el-textarea__inner {
  font-family: inherit;
}

/* Dialog adjustments */
::v-deep .el-dialog__header {
  padding: 0;
}

::v-deep .el-dialog__body {
  padding: 20px;
}

/* Card hover effect */
.group:hover {
  transform: translateY(-4px);
}

/* Button group spacing */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.masonry {
  column-count: 3;
  column-gap: 1rem;
}

.card {
  break-inside: avoid;
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 2px;
  box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
}

/* Responsive */
@media (max-width: 1024px) {
  .masonry { column-count: 3; }
}

@media (max-width: 768px) {
  .masonry { column-count: 2; }
}

@media (max-width: 480px) {
  .masonry { column-count: 1; }
}
</style>